import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/PageTitle/PageTitle";
import Table from "../../components/Table/Table";
import { useEffect, useState, useCallback } from "react";
import { httprequest } from "../../data/api";
import { toast, ToastContainer } from "react-toastify";
import PageLoader from "../../components/PageLoader/pageloader";
import { Box, Button, Drawer, IconButton, Pagination, TextField, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";

const ENDPOINT = "/admin/v1/signup-attribution";
const PAGE_SIZE = 20;

const emptyFilters = () => ({
  search: "",
  date_from: "",
  date_to: ""
});

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }

  return d.toLocaleString(navigator.language, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const ellipsize = (s, max = 48) => {
  if (s == null || s === "") {
    return "-";
  }
  const t = String(s);

  return t.length > max ? `${t.slice(0, max)}…` : t;
};

const Marketing = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        ...appliedFilters
      };

      const clean = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== "" && v != null)
      );
      const res = await httprequest.get(ENDPOINT, { params: clean });
      const payload = res?.data?.data;
      const list = Array.isArray(payload?.rows) ? payload.rows : [];
      const pag = payload?.pagination || {};

      setRows(
        list.map((row) => ({
          ...row,
          created_display: formatDate(row.created_at),
          referrer_short: ellipsize(row.referrer, 40),
          path_short: ellipsize(row.captured_page_path, 36)
        }))
      );
      setTotalPages(Number(pag.total_pages) || 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load signup attribution");
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDraftChange = (field) => (e) => {
    setDraftFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
  };

  const handleResetFilters = () => {
    const cleared = emptyFilters();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  };

  const handleOpenDetails = (row) => {
    setSelectedRow(row);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedRow(null);
  };

  const columns = [
    { name: "School", selector: (row) => row.school_name || "-", sortable: true, grow: 1 },
    { name: "Email", selector: (row) => row.email || "-", sortable: true, grow: 1 },
    { name: "Phone Number", selector: (row) => row.phone_number || "-", sortable: true },
    { name: "utm_source", selector: (row) => row.utm_source || "-", sortable: true },
    { name: "utm_medium", selector: (row) => row.utm_medium || "-", sortable: true },
    { name: "utm_campaign", selector: (row) => row.utm_campaign || "-", sortable: true },
    { name: "Created", selector: (row) => row.created_display, sortable: true },
    {
      name: "Action",
      sortable: false,
      width: "96px",
      cell: (row) => (
        <IconButton
          aria-label="View signup attribution details"
          size="small"
          onClick={() => handleOpenDetails(row)}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      )
    }
  ];

  const filterBar = (
    <Box sx={{ width: "100%", py: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: { xs: "wrap", lg: "nowrap" }
        }}
      >
        <TextField
          label="Search (school, email, phone, campaign, source)"
          value={draftFilters.search}
          onChange={handleDraftChange("search")}
          size="small"
          variant="outlined"
          sx={{ flex: "1 1 auto", minWidth: { xs: "100%", lg: 280 } }}
        />
        <TextField
          label="Date from"
          type="date"
          value={draftFilters.date_from}
          onChange={handleDraftChange("date_from")}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: "calc(50% - 8px)", lg: 170 } }}
        />
        <TextField
          label="Date to"
          type="date"
          value={draftFilters.date_to}
          onChange={handleDraftChange("date_to")}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: "calc(50% - 8px)", lg: 170 } }}
        />
        <Box
          sx={{
            marginLeft: { xs: 0, lg: "auto" },
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            width: { xs: "100%", lg: "auto" }
          }}
        >
          <Button variant="contained" onClick={handleApplyFilters} sx={{ minWidth: 130, flex: { xs: 1, lg: "none" } }}>
            Apply filters
          </Button>
          <Button variant="outlined" onClick={handleResetFilters} sx={{ minWidth: 100, flex: { xs: 1, lg: "none" } }}>
            Reset
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Layout>
      <PageTitle
        title="Marketing"
        subtitle="School signups with UTM and campaign attribution (first-touch capture on signup)."
      />

      {loading ? (
        <PageLoader />
      ) : (
        <>
          <Table
            data={rows}
            columns={columns}
            subHeader
            subHeaderComponent={filterBar}
            pagination={false}
          />
          {totalPages > 1 ? (
            <Box display="flex" justifyContent="center" py={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : null}
        </>
      )}
      <Drawer anchor="right" open={detailsOpen} onClose={handleCloseDetails}>
        <Box sx={{ width: 420, p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">
              Signup Attribution Details
            </Typography>
            <IconButton aria-label="Close details drawer" size="small" onClick={handleCloseDetails}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>School:</strong> {selectedRow?.school_name || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Email:</strong> {selectedRow?.email || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Phone Number:</strong> {selectedRow?.phone_number || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>utm_source:</strong> {selectedRow?.utm_source || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>utm_medium:</strong> {selectedRow?.utm_medium || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>utm_campaign:</strong> {selectedRow?.utm_campaign || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>utm_term:</strong> {selectedRow?.utm_term || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>utm_content:</strong> {selectedRow?.utm_content || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Referrer:</strong> {selectedRow?.referrer || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Landing path:</strong> {selectedRow?.captured_page_path || "-"}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Created:</strong> {selectedRow?.created_display || "-"}
          </Typography>
        </Box>
      </Drawer>
      <ToastContainer />
    </Layout>
  );
};

export default Marketing;
